import React, { useEffect, useState } from 'react'
import { actionAtom } from 'atoms/atoms'
import { useAtom, useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useDebounce } from '@node-space/hooks'
import PageHeader from '@node-space/storybook-components/dist/PageHeader'
import { StatusCard } from '@node-space/storybook-components/dist/StatusCard'
import { Text } from '@node-space/storybook-components/dist/Text'
import { TextAction } from '@node-space/storybook-components/dist/TextAction'
import { Box, Button, Box as Container, Icon, Input } from '@node-space/ui-components'
import NoSearchResults from 'components/NoSearchResults/NoSearchResults'
import TablePagination from 'components/PageableTablePagination'
import { Description } from 'components/text/Description'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useContactsQuery } from 'hooks/queries/useContactsQuery'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { usePaginationPagingPersist } from 'hooks/usePaginationPagingPersist'
import { PaginationPaging } from 'types/pagination'
import { ActionIds } from 'types/types'
import { contactAtom } from './atoms/contactAtoms'
import { ContactsSheets } from './components/ContactsSheets'
import { ContactsList } from './ContactsList'
import { ContactsListSkeletonLoader } from './ContactsListSkeletonLoader'

export const Contacts = () => {
  const { t } = useTranslation()
  const { viewOnly } = useProfileContext() || {}
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(0)
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const debouncedSearchTerm = useDebounce(searchQuery, 1000)
  const permissions = useMappedRoles()?.contacts
  const canEdit = permissions?.canEdit
  const [action, setAction] = useAtom(actionAtom)
  const setContact = useSetAtom(contactAtom)

  const {
    data: { content: contactList, pagination } = {},
    isPending,
    isError,
  } = useContactsQuery({ size: 10, page, searchQuery: debouncedSearchTerm })

  const { paging, showPagination } = usePaginationPagingPersist(pagination)
  const noResults = debouncedSearchTerm && !contactList?.length
  const showEmptyState = !isPending && !isError && !contactList?.length && !debouncedSearchTerm
  const showErrorState = isError && !isPending
  const showNoSearchResults = noResults && !isPending
  const showHeaderButton = (canEdit && !!contactList?.length) || (noResults && canEdit)
  const showSearch = !!contactList?.length || noResults

  useEffect(() => {
    setContact(null)
  }, [])

  const triggerOpenSheet = () => {
    setAction({
      ...action,
      actionId: ActionIds?.CONTACT,
      header: t('contacts.addCryptoContact'),
      onClose: () => {
        setIsSheetOpen(false)
        setContact(null)
      },
      meta: {
        enableCreateInstrument: true,
      },
    })
    setIsSheetOpen(true)
  }

  return (
    <Box>
      <PageHeader
        description={
          <Text size="sm" color="grey">
            {t('contacts.description')}
          </Text>
        }
        renderRightSection={
          showHeaderButton && (
            <Button
              testId="add-contact"
              disabled={viewOnly}
              onClick={triggerOpenSheet}
              iconElement={<Icon name="Plus" />}
            >
              {t('contacts.addContact')}
            </Button>
          )
        }
      />
      {/* Show error state */}
      {showErrorState && (
        <StatusCard status="error" headline={t('defaultError')}>
          <Description>
            {`${t('requestErrors.comeBackLater')} `}
            <TextAction isWindowReload text={t('requestErrors.refreshPage')} />.
          </Description>
        </StatusCard>
      )}
      {/* Show content state */}
      {!isError && (
        <>
          {showSearch && (
            <Box paddingB={16} width="full">
              <Input
                placeholderText={t('contacts.searchPlaceholder')}
                onChange={event => setSearchQuery(event.target.value)}
                searchable={true}
                testId="contact-search-field"
              />
            </Box>
          )}

          {isPending && <ContactsListSkeletonLoader />}

          {showNoSearchResults && (
            <NoSearchResults
              clearSearchCriteria={() => {
                setSearchQuery('')
              }}
            />
          )}

          {!!contactList?.length && !isPending && (
            <Box className="w-full">
              <ContactsList contacts={contactList} setIsSheetOpen={setIsSheetOpen} />
            </Box>
          )}

          {!!paging && !showPagination && (
            <Container className={`${isPending ? 'opacity-25' : ''}`}>
              <TablePagination
                disableLeftDropdown
                total={paging?.total}
                pagingMax={paging?.max}
                paging={paging}
                onPagination={({ page }: PaginationPaging) => setPage(page)}
                resetPageNumber={debouncedSearchTerm}
              />
            </Container>
          )}
        </>
      )}
      {/* Show empty state */}
      {showEmptyState && (
        <StatusCard
          headline={t('contacts.noContactHeadline')}
          description={t('contacts.addContactDescription')}
          cardButton={
            canEdit && {
              testid: 'add-contact',
              disabled: viewOnly,
              onClick: triggerOpenSheet,
              iconElement: <Icon name="Plus" />,
              name: t('contacts.addContact'),
            }
          }
        />
      )}
      <ContactsSheets isSheetOpen={isSheetOpen} />
    </Box>
  )
}
